/**
 * Kirjanpito-dashboard (laaja).
 *
 * - KPI: liikevaihto, kulut, tulos, ALV-vastuu
 * - Tabs: Yhteenveto / Päiväkirja / Tase / Tuloslaskelma / Arkisto
 * - "Aja raportti" -nappi → /api/accounting/sync (laskut + e-kuitit)
 * - Lataa PDF, lähetä sähköpostilla
 * - Manuaalinen kirjaus (Add/Edit/Delete)
 * - Auto-merkityt kirjaukset näkyvät "Auto"-badge:lla
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Calculator, BookOpen, RefreshCw, Plus, Download, Mail, Edit, Trash2,
  Loader2, TrendingUp, TrendingDown, ScrollText, FileBarChart, Users,
} from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/browser";
import { ModuleSettingsButton } from "@/components/admin/FactoryResetDialog";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";

const eur = (v) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

function monthOptions() {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("fi-FI", { month: "long", year: "numeric" }),
    });
  }
  return arr;
}

const initEntry = () => ({
  entry_date: today(),
  description: "",
  entry_type: "sale",
  amount_net: "",
  vat_percent: 24,
  counterparty: "",
  reference: "",
  notes: "",
  account_code: "",
});

const ENTRY_TYPES = [
  { value: "sale", label: "Myynti" },
  { value: "income", label: "Muu tulo" },
  { value: "purchase", label: "Osto" },
  { value: "expense", label: "Kulu" },
  { value: "other", label: "Muu" },
];

export default function AccountingDashboard() {
  const [month, setMonth] = useState(currentMonth());
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [archive, setArchive] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [entryForm, setEntryForm] = useState(initEntry());

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, sRes, bRes, mRes] = await Promise.all([
        api.get(`/accounting?month=${month}`),
        api.get(`/accounting/summary?month=${month}`),
        api.get("/accounting/balance-sheet"),
        api.get("/accounting/monthly"),
      ]);
      setEntries(eRes.data);
      setSummary(sRes.data);
      setBalance(bRes.data);
      setArchive(mRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, [month]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/accounting/sync?month=${month}`);
      toast.success(
        `Synkronoitu: ${r.data.invoices_synced} laskua → ${r.data.invoice_entries_created} kirjausta, ${r.data.receipts_synced} kuittia → ${r.data.receipt_entries_created} kirjausta`,
      );
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSyncing(false);
  };

  const saveEntry = async () => {
    try {
      const payload = {
        ...entryForm,
        amount_net: parseFloat(entryForm.amount_net) || 0,
        vat_percent: parseFloat(entryForm.vat_percent) || 0,
      };
      if (entryForm.id) {
        await api.put(`/accounting/${entryForm.id}`, payload);
        toast.success("Päivitetty");
      } else {
        await api.post("/accounting", payload);
        toast.success("Lisätty");
      }
      setShowForm(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Poistetaanko kirjaus?")) return;
    try {
      await api.delete(`/accounting/${id}`);
      toast.success("Poistettu");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadReport = async (kind) => {
    const map = {
      journal: { url: `/accounting/journal/pdf?month=${month}`, name: `paivakirja-${month}.pdf` },
      balance: { url: `/accounting/balance-sheet/pdf`, name: `tase.pdf` },
      income: { url: `/accounting/income-statement/pdf?month=${month}`, name: `tuloslaskelma-${month}.pdf` },
    };
    const cfg = map[kind];
    try {
      const r = await api.get(cfg.url, { responseType: "blob" });
      downloadBlob(r.data, cfg.name);
      toast.success("PDF ladattu");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const emailReport = async (report) => {
    const to = window.prompt("Vastaanottajan sähköposti:", "");
    if (!to) return;
    try {
      const r = await api.post("/accounting/email-report", { to, report, month });
      if (r.data.status === "mock") {
        toast.warning("Sähköposti kirjattu (SMTP ei vielä konffattu)");
      } else {
        toast.success(`Lähetetty: ${to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-lg">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Moduuli</p>
            <h1 className="text-3xl font-display gradient-text-luxe">Kirjanpito</h1>
            <p className="text-white/60 text-sm mt-1">
              Päiväkirja, tase, tuloslaskelma. Auto-synkronoitu laskutuksen ja e-kuittien kanssa.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ModuleSettingsButton moduleKey="accounting" moduleLabel="Kirjanpito" onAfterReset={loadAll} />
          <Button
            onClick={runSync}
            disabled={syncing}
            variant="outline"
            className="border-emerald-500/30 text-emerald-200 inline-flex items-center gap-2"
            data-testid="accounting-sync-button"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Aja raportti (sync)
          </Button>
          <Button
            onClick={() => { setEntryForm(initEntry()); setShowForm(true); }}
            className="btn-luxe inline-flex items-center gap-2"
            data-testid="accounting-add-entry"
          >
            <Plus className="w-4 h-4" /> Uusi kirjaus
          </Button>
        </div>
      </header>

      {/* Month picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-white/60">Kuukausi</Label>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-input/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
          data-testid="accounting-month-select"
        >
          {monthOptions().map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Liikevaihto" value={eur(summary?.revenue_net)} accent="text-emerald-300" testid="accounting-kpi-revenue" />
        <Kpi icon={TrendingDown} label="Kulut" value={eur(summary?.costs_net)} accent="text-rose-300" testid="accounting-kpi-costs" />
        <Kpi
          icon={FileBarChart}
          label="Tulos"
          value={eur(summary?.profit_net)}
          accent={(summary?.profit_net ?? 0) >= 0 ? "text-amber-300" : "text-red-300"}
          testid="accounting-kpi-profit"
        />
        <Kpi icon={ScrollText} label="ALV-vastuu" value={eur(summary?.vat_payable)} accent="text-violet-300" testid="accounting-kpi-vat" />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/[0.04] border border-white/5">
          <TabsTrigger value="overview" data-testid="accounting-tab-overview">Yhteenveto</TabsTrigger>
          <TabsTrigger value="journal" data-testid="accounting-tab-journal">Päiväkirja</TabsTrigger>
          <TabsTrigger value="income" data-testid="accounting-tab-income">Tuloslaskelma</TabsTrigger>
          <TabsTrigger value="balance" data-testid="accounting-tab-balance">Tase</TabsTrigger>
          <TabsTrigger value="archive" data-testid="accounting-tab-archive">Arkisto</TabsTrigger>
          <TabsTrigger value="staff" data-testid="accounting-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewView summary={summary} entries={entries} />
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          <JournalView
            entries={entries}
            loading={loading}
            onEdit={(e) => { setEntryForm({ ...e, amount_net: e.amount_net, vat_percent: e.vat_percent }); setShowForm(true); }}
            onDelete={deleteEntry}
            onPdf={() => downloadReport("journal")}
            onEmail={() => emailReport("journal")}
          />
        </TabsContent>

        <TabsContent value="income" className="mt-4">
          <IncomeStatementView
            summary={summary}
            onPdf={() => downloadReport("income")}
            onEmail={() => emailReport("income_statement")}
          />
        </TabsContent>

        <TabsContent value="balance" className="mt-4">
          <BalanceSheetView
            balance={balance}
            onPdf={() => downloadReport("balance")}
            onEmail={() => emailReport("balance_sheet")}
          />
        </TabsContent>

        <TabsContent value="archive" className="mt-4">
          <ArchiveTable rows={archive?.months || []} onPick={(m) => { setMonth(m); setTab("journal"); }} />
        </TabsContent>

        <TabsContent value="staff" className="mt-4">
          <ModuleStaffTab moduleKey="accounting" moduleLabel="Kirjanpito" />
        </TabsContent>
      </Tabs>

      {/* Entry form dialog */}
      <Dialog open={showForm} onOpenChange={(v) => !v && setShowForm(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{entryForm.id ? "Muokkaa kirjausta" : "Uusi kirjaus"}</DialogTitle>
            <DialogDescription>
              Manuaalinen kirjaus täydentää auto-synkronoituja kirjauksia.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Päivä" type="date" value={entryForm.entry_date} onChange={(v) => setEntryForm((f) => ({ ...f, entry_date: v }))} testid="accounting-field-date" />
            <div>
              <Label className="text-xs">Tyyppi</Label>
              <select
                value={entryForm.entry_type}
                onChange={(e) => setEntryForm((f) => ({ ...f, entry_type: e.target.value }))}
                className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="accounting-field-type"
              >
                {ENTRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Field label="Selite" value={entryForm.description} onChange={(v) => setEntryForm((f) => ({ ...f, description: v }))} testid="accounting-field-description" />
            </div>
            <Field label="Vastapuoli" value={entryForm.counterparty} onChange={(v) => setEntryForm((f) => ({ ...f, counterparty: v }))} />
            <Field label="Viite" value={entryForm.reference} onChange={(v) => setEntryForm((f) => ({ ...f, reference: v }))} />
            <Field label="Veroton €" type="number" value={entryForm.amount_net} onChange={(v) => setEntryForm((f) => ({ ...f, amount_net: v }))} testid="accounting-field-net" />
            <div>
              <Label className="text-xs">ALV %</Label>
              <select
                value={entryForm.vat_percent}
                onChange={(e) => setEntryForm((f) => ({ ...f, vat_percent: e.target.value }))}
                className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="accounting-field-vat"
              >
                {[0, 10, 14, 24, 25.5].map((r) => <option key={r} value={r}>{r} %</option>)}
              </select>
            </div>
            <Field label="Tilinumero (valinnainen)" value={entryForm.account_code} onChange={(v) => setEntryForm((f) => ({ ...f, account_code: v }))} />
            <div className="col-span-2">
              <Label className="text-xs">Muistiinpanot</Label>
              <textarea
                value={entryForm.notes}
                onChange={(e) => setEntryForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]"
              />
            </div>
            <div className="col-span-2 flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowForm(false)} className="flex-1">Peruuta</Button>
              <Button onClick={saveEntry} className="btn-luxe flex-1" data-testid="accounting-save">Tallenna</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -- Sub-components ----------------------------------------------------------
function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="stat-block" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <p className={`text-2xl font-display font-bold mt-1 ${accent}`}>{value}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        step={type === "number" ? "0.01" : undefined}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}

function OverviewView({ summary, entries }) {
  if (!summary) return <div className="glass-card p-8 text-center text-white/40">Ei dataa.</div>;
  const recent = (entries || []).slice(0, 8);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="glass-card p-6 space-y-2">
        <h3 className="text-sm font-semibold text-white/80 mb-3">ALV-erittely</h3>
        {(summary.vat_breakdown || []).length === 0 ? (
          <p className="text-white/40 text-sm">Ei kirjauksia.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase text-white/40 border-b border-white/5">
                <th className="text-left py-2">ALV %</th>
                <th className="text-right">Veroton</th>
                <th className="text-right">ALV</th>
              </tr>
            </thead>
            <tbody>
              {summary.vat_breakdown.map((v) => (
                <tr key={v.vat_percent} className="border-b border-white/5">
                  <td className="py-2 text-white/80">{v.vat_percent} %</td>
                  <td className="text-right text-white/70 font-mono">{eur(v.net)}</td>
                  <td className="text-right text-violet-300 font-mono">{eur(v.vat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="glass-card p-6 space-y-2">
        <h3 className="text-sm font-semibold text-white/80 mb-3">Viimeisimmät kirjaukset</h3>
        {recent.length === 0 ? (
          <p className="text-white/40 text-sm">Ei kirjauksia.</p>
        ) : (
          <ul className="space-y-2">
            {recent.map((e) => (
              <li key={e.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <div>
                  <p className="text-white/90">{e.description}</p>
                  <p className="text-[10px] text-white/40">{e.entry_date} · {e.entry_type}</p>
                </div>
                <span className={`font-mono ${["sale", "income"].includes(e.entry_type) ? "text-emerald-300" : "text-rose-300"}`}>
                  {eur(e.amount_gross)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function JournalView({ entries, loading, onEdit, onDelete, onPdf, onEmail }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onPdf} className="inline-flex items-center gap-2" data-testid="accounting-pdf-journal">
          <Download className="w-3 h-3" /> Lataa PDF
        </Button>
        <Button variant="outline" size="sm" onClick={onEmail} className="inline-flex items-center gap-2" data-testid="accounting-email-journal">
          <Mail className="w-3 h-3" /> Lähetä
        </Button>
      </div>
      <div className="glass-card overflow-hidden p-0">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-white/40">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-white/20" />
            <p>Ei kirjauksia. Aja raportti (sync) tai lisää käsin.</p>
          </div>
        ) : (
          <table className="w-full text-sm relative z-10">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                <th className="py-3 px-4">Pvm</th>
                <th className="py-3 px-4">Selite</th>
                <th className="py-3 px-4">Tyyppi</th>
                <th className="py-3 px-4">Vastapuoli</th>
                <th className="py-3 px-4 text-right">Veroton</th>
                <th className="py-3 px-4 text-right">ALV</th>
                <th className="py-3 px-4 text-right">Brutto</th>
                <th className="py-3 px-4 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`accounting-row-${e.id}`}>
                  <td className="py-3 px-4 text-white/80">{(e.entry_date || "").slice(0, 10)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{e.description}</span>
                      {e.auto && (
                        <Badge className="text-[9px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          auto · {e.source}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={`text-[10px] ${["sale", "income"].includes(e.entry_type) ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-rose-500/15 text-rose-300 border-rose-500/30"} border`}>
                      {e.entry_type}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-white/70">{e.counterparty || "—"}</td>
                  <td className="py-3 px-4 text-right text-white/70 font-mono">{eur(e.amount_net)}</td>
                  <td className="py-3 px-4 text-right text-violet-300 font-mono">{eur(e.amount_vat)}</td>
                  <td className="py-3 px-4 text-right text-amber-300 font-mono font-semibold">{eur(e.amount_gross)}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 justify-end">
                      {!e.auto && (
                        <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => onEdit(e)} data-testid={`accounting-edit-${e.id}`}>
                          <Edit className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="w-7 h-7 text-red-300" onClick={() => onDelete(e.id)} data-testid={`accounting-delete-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function IncomeStatementView({ summary, onPdf, onEmail }) {
  if (!summary) return <div className="glass-card p-8 text-center text-white/40">Ei dataa.</div>;
  const rows = [
    { label: "Liikevaihto (veroton)", value: summary.revenue_net, bold: false },
    { label: "Muut tuotot", value: 0 },
    { label: "Tuotot yhteensä", value: summary.revenue_net, bold: true, divider: true },
    { label: "Materiaalit ja palvelut", value: -summary.costs_net },
    { label: "Henkilöstökulut", value: 0 },
    { label: "Muut kulut", value: 0 },
    { label: "Kulut yhteensä", value: -summary.costs_net, bold: true, divider: true },
    { label: "TULOS", value: summary.profit_net, bold: true, big: true },
  ];
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onPdf} className="inline-flex items-center gap-2" data-testid="accounting-pdf-income">
          <Download className="w-3 h-3" /> Lataa PDF
        </Button>
        <Button variant="outline" size="sm" onClick={onEmail} className="inline-flex items-center gap-2">
          <Mail className="w-3 h-3" /> Lähetä
        </Button>
      </div>
      <div className="glass-card p-6">
        <h3 className="text-lg font-display text-amber-300 mb-4">Tuloslaskelma · {summary.month}</h3>
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={r.divider ? "border-t border-white/10" : ""}>
                <td className={`py-2 ${r.bold ? "font-bold text-white" : "text-white/80"} ${r.big ? "text-base" : ""}`}>
                  {r.label}
                </td>
                <td className={`py-2 text-right font-mono ${r.bold ? "font-bold" : ""} ${r.value >= 0 ? "text-emerald-300" : "text-rose-300"} ${r.big ? "text-base text-amber-300" : ""}`}>
                  {eur(r.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BalanceSheetView({ balance, onPdf, onEmail }) {
  if (!balance) return <div className="glass-card p-8 text-center text-white/40">Ei dataa.</div>;
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onPdf} className="inline-flex items-center gap-2" data-testid="accounting-pdf-balance">
          <Download className="w-3 h-3" /> Lataa PDF
        </Button>
        <Button variant="outline" size="sm" onClick={onEmail} className="inline-flex items-center gap-2">
          <Mail className="w-3 h-3" /> Lähetä
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-widest mb-3">Vastaavaa</h3>
          <table className="w-full text-sm">
            <tbody>
              <BalanceRow label="Pankkitili (maksetut laskut)" value={balance.bank} />
              <BalanceRow label="Myyntisaamiset" value={balance.receivables} />
              <BalanceRow label="Muut omaisuuserät" value={balance.other_assets} />
              <BalanceRow label="Yhteensä" value={balance.bank + balance.receivables + balance.other_assets} bold />
            </tbody>
          </table>
        </div>
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-rose-300 uppercase tracking-widest mb-3">Vastattavaa</h3>
          <table className="w-full text-sm">
            <tbody>
              <BalanceRow label="Ostovelat (kulut)" value={balance.payables} />
              <BalanceRow label="ALV-velka" value={balance.vat_liability} />
              <BalanceRow label="Oma pääoma (laskennallinen)" value={balance.equity} />
              <BalanceRow label="Yhteensä" value={balance.payables + balance.vat_liability + balance.equity} bold />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BalanceRow({ label, value, bold }) {
  return (
    <tr className={bold ? "border-t border-white/10" : ""}>
      <td className={`py-2 ${bold ? "font-bold text-white" : "text-white/80"}`}>{label}</td>
      <td className={`py-2 text-right font-mono ${bold ? "font-bold text-amber-300" : "text-white/70"}`}>
        {eur(value)}
      </td>
    </tr>
  );
}

function ArchiveTable({ rows, onPick }) {
  if (rows.length === 0) {
    return <div className="glass-card p-8 text-center text-white/40">Ei arkistodataa tältä vuodelta.</div>;
  }
  return (
    <div className="glass-card overflow-hidden p-0">
      <table className="w-full text-sm relative z-10">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            <th className="py-3 px-4">Kuukausi</th>
            <th className="py-3 px-4 text-right">Kirjauksia</th>
            <th className="py-3 px-4 text-right">Liikevaihto</th>
            <th className="py-3 px-4 text-right">Kulut</th>
            <th className="py-3 px-4 text-right">Tulos</th>
            <th className="py-3 px-4 text-right">ALV-vastuu</th>
            <th className="py-3 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="py-3 px-4 text-white">{r.month}</td>
              <td className="py-3 px-4 text-right">{r.count}</td>
              <td className="py-3 px-4 text-right text-emerald-300 font-mono">{eur(r.revenue_net)}</td>
              <td className="py-3 px-4 text-right text-rose-300 font-mono">{eur(r.costs_net)}</td>
              <td className={`py-3 px-4 text-right font-mono font-semibold ${(r.profit_net || 0) >= 0 ? "text-amber-300" : "text-red-300"}`}>
                {eur(r.profit_net)}
              </td>
              <td className="py-3 px-4 text-right text-violet-300 font-mono">{eur(r.vat_payable)}</td>
              <td className="py-3 px-4">
                <Button size="sm" variant="ghost" onClick={() => onPick(r.month)}>Avaa</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
